import { format, formatDistanceStrict, parseISO } from 'date-fns';
import type { Money } from '@micarahr/shared-types';

/**
 * Formatting.
 *
 * One definition per format. A payroll figure rendered two ways on two screens
 * is a support call about a discrepancy that does not exist.
 */

export function formatMoney(
  money: Money | null | undefined,
  options: { compact?: boolean } = {},
): string {
  if (!money) return '—';
  const pesos = money.minorUnits / 100;
  if (options.compact && Math.abs(pesos) >= 1_000_000) {
    return `₱${(pesos / 1_000_000).toFixed(1)}M`;
  }
  if (options.compact && Math.abs(pesos) >= 1_000) {
    return `₱${(pesos / 1_000).toFixed(0)}K`;
  }
  return `₱${pesos.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * A peso amount as a chart axis tick: "₱0", "₱500K", "₱1M", "₱1.5M".
 *
 * Ticks are read at a glance, so they carry as few digits as say the value —
 * "₱1000K" makes the reader do the division the axis should have done.
 */
export function formatPesoTick(pesos: number): string {
  const magnitude = Math.abs(pesos);
  const trim = (value: number) => String(Number(value.toFixed(1)));
  if (magnitude >= 1_000_000_000) return `₱${trim(pesos / 1_000_000_000)}B`;
  if (magnitude >= 1_000_000) return `₱${trim(pesos / 1_000_000)}M`;
  if (magnitude >= 1_000) return `₱${trim(pesos / 1_000)}K`;
  return `₱${trim(pesos)}`;
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'dd MMM yyyy');
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'dd MMM yyyy, h:mm a');
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'h:mm a');
}

/**
 * A wall-clock time ("18:00", a shift's start or an overtime window) as every
 * other time on the console reads: "6:00 PM". Not a date — no timezone is
 * applied, because a shift's hours are the location's local hours already.
 */
export function formatClockTime(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return value;
  const hours = Number(match[1]);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 === 0 ? 12 : hours % 12}:${match[2]} ${suffix}`;
}

/**
 * "12 minutes ago". Whole units, rounded down: date-fns rounds by default, so
 * 59.6 seconds read "60 seconds ago" (and 59.6 minutes "60 minutes ago")
 * instead of moving up to "1 minute ago". A time a moment ahead of the
 * browser's clock — the server's clock runs slightly fast — is "just now",
 * not "in 2 seconds ago".
 */
export function formatRelative(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '—';
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return '—';
  if (date.getTime() >= now.getTime()) return 'just now';
  return `${formatDistanceStrict(date, now, { roundingMethod: 'floor' })} ago`;
}

/** `pending_review` → `Pending review`. Sentence case, matching the mobile app. */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (!words) return '—';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The indefinite article a phrase takes when it is spoken: "an initial
 * interview", "a panel interview", "a one-time adjustment". Decided by sound,
 * so the few vowel letters that start with a consonant sound ("one", "uni",
 * "use", "eu") take "a", and a silent "h" ("hour", "honest") takes "an".
 */
export function withArticle(phrase: string): string {
  const word = phrase.trim().toLowerCase();
  const an =
    /^(hour|honest|honou?r|heir)/.test(word) ||
    (/^[aeiou]/.test(word) && !/^(one|once|uni|use|usu|uti|eu|ewe)/.test(word));
  return `${an ? 'an' : 'a'} ${phrase.trim()}`;
}

export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || minutes === 0) return '—';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}m`;
  if (remainder === 0) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

export function formatHours(hours: number | null | undefined): string {
  if (!hours) return '—';
  return `${hours}h`;
}

export function formatAge(hours: number): string {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/** Ages an item into a tone so a two-week-old approval looks different to today's. */
export function ageTone(hours: number): 'neutral' | 'warning' | 'danger' {
  if (hours > 168) return 'danger';
  if (hours > 72) return 'warning';
  return 'neutral';
}

/** CSV export. Values are quoted so a comma in a name cannot break a column. */
export function toCsv(
  rows: Record<string, unknown>[],
  columns: { key: string; header: string }[],
): string {
  const escape = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const header = columns.map((column) => escape(column.header)).join(',');
  const body = rows.map((row) => columns.map((column) => escape(row[column.key])).join(','));
  return [header, ...body].join('\n');
}

export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
